import { useCallback, useRef, useState, type DragEvent } from "react";
import { Card } from "./Card";
import { FileIcon, TrashIcon, UploadIcon } from "./Icons";
import { formatOffset } from "../flasher/chips";
import { formatBytes } from "../flasher/binaryInfo";
import { ROLE_LABELS, type FirmwarePart, type LayoutReport } from "../flasher/layout";

interface Props {
  parts: FirmwarePart[];
  report: LayoutReport;
  isReading: boolean;
  onAddFiles: (files: File[]) => void;
  onDropTransfer: (transfer: DataTransfer) => void;
  onRemove: (id: string) => void;
  onClear: () => void;
  onOffsetChange: (id: string, offset: number) => void;
  onOffsetReset: (id: string) => void;
  onToggle: (id: string) => void;
}

/** Accepts "0x1000", "1000" (interpreted as hex), or decimal with a trailing 'd'. */
function parseOffset(input: string): number | null {
  const text = input.trim().toLowerCase();
  if (text.length === 0) return null;

  if (text.endsWith("d")) {
    const decimal = Number(text.slice(0, -1));
    return Number.isInteger(decimal) && decimal >= 0 ? decimal : null;
  }

  const hex = text.startsWith("0x") ? text.slice(2) : text;
  if (!/^[0-9a-f]+$/.test(hex)) return null;
  const value = Number.parseInt(hex, 16);
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

export function FirmwareCard({
  parts,
  report,
  isReading,
  onAddFiles,
  onDropTransfer,
  onRemove,
  onClear,
  onOffsetChange,
  onOffsetReset,
  onToggle,
}: Props) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Nested dragenter/dragleave events fire constantly while moving over
  // children; counting them keeps the highlight from flickering.
  const dragDepth = useRef(0);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragDepth.current = 0;
      setDragActive(false);
      onDropTransfer(event.dataTransfer);
    },
    [onDropTransfer],
  );

  const included = parts.filter((part) => part.include);

  return (
    <Card
      step={2}
      done={included.length > 0 && !report.hasErrors}
      title="Firmware"
      hint="Drop the .bin files exported from the Arduino IDE. Offsets are worked out for you."
      actions={
        parts.length > 0 ? (
          <button className="btn btn--ghost btn--sm" onClick={onClear}>
            Clear all
          </button>
        ) : undefined
      }
    >
      <div
        className={`dropzone${dragActive ? " dropzone--active" : ""}`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          dragDepth.current += 1;
          setDragActive(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          event.preventDefault();
          dragDepth.current -= 1;
          if (dragDepth.current <= 0) setDragActive(false);
        }}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        aria-label="Add firmware files"
      >
        <div className="dropzone__icon">
          <UploadIcon size={26} />
        </div>
        <div className="dropzone__title">
          {isReading ? "Reading files..." : "Drop .bin files or the whole build folder"}
        </div>
        <p className="dropzone__hint">
          In the Arduino IDE use <strong>Sketch &rsaquo; Export Compiled Binary</strong>, then drop the exported
          folder here. Bootloader, partition table, and application are recognised by name and placed automatically.
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".bin,application/octet-stream"
          className="sr-only"
          onChange={(event) => {
            onAddFiles(Array.from(event.target.files ?? []));
            // Reset so re-picking the same file still fires a change event.
            event.target.value = "";
          }}
        />
      </div>

      {parts.length > 0 && (
        <div className="parts">
          {parts.map((part) => {
            const flagged = report.issues.some(
              (issue) => issue.level === "error" && issue.partIds.includes(part.id),
            );
            const suggested = part.analysis.suggestedOffset;
            const isCustom = part.offsetSource === "manual" && suggested !== null && part.offset !== suggested;

            return (
              <div
                key={part.id}
                className={`part${flagged ? " part--flagged" : ""}${part.include ? "" : " part--excluded"}`}
              >
                <div className="part__main">
                  <input
                    type="checkbox"
                    className="part__check"
                    checked={part.include}
                    onChange={() => onToggle(part.id)}
                    aria-label={`Include ${part.fileName}`}
                  />
                  <span className="part__glyph">
                    <FileIcon size={15} />
                  </span>
                  <span className="part__name" title={part.fileName}>
                    {part.fileName}
                  </span>

                  <span className={`pill ${rolePillClass(part.analysis.role)}`}>
                    {ROLE_LABELS[part.analysis.role]}
                  </span>

                  <div className="part__offset">
                    <label className="sr-only" htmlFor={`offset-${part.id}`}>
                      Flash offset for {part.fileName}
                    </label>
                    <input
                      id={`offset-${part.id}`}
                      defaultValue={formatOffset(part.offset)}
                      key={`${part.id}-${part.offset}`}
                      disabled={part.analysis.offsetIsFixed}
                      title={
                        part.analysis.offsetIsFixed
                          ? "This address is fixed by the boot ROM and cannot be changed."
                          : "Flash offset. Hex by default; append 'd' for decimal."
                      }
                      onBlur={(event) => {
                        const parsed = parseOffset(event.target.value);
                        if (parsed === null) {
                          event.target.value = formatOffset(part.offset);
                          return;
                        }
                        if (parsed !== part.offset) onOffsetChange(part.id, parsed);
                        else event.target.value = formatOffset(part.offset);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                      }}
                    />
                  </div>

                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => onRemove(part.id)}
                    aria-label={`Remove ${part.fileName}`}
                  >
                    <TrashIcon size={14} />
                  </button>
                </div>

                <div className="part__meta">
                  <span>{formatBytes(part.data.length)}</span>
                  <span>&middot;</span>
                  <span>identified by {describeBasis(part.analysis.basis)}</span>
                  {part.analysis.declaredChip && (
                    <>
                      <span>&middot;</span>
                      <span>built for {part.analysis.declaredChip.name}</span>
                    </>
                  )}
                  {part.analysis.header?.flashMode && (
                    <>
                      <span>&middot;</span>
                      <span>
                        {part.analysis.header.flashMode}
                        {part.analysis.header.flashFrequency ? ` @ ${part.analysis.header.flashFrequency}` : ""}
                      </span>
                    </>
                  )}
                  {isCustom && (
                    <>
                      <span>&middot;</span>
                      <button className="btn btn--ghost btn--sm" onClick={() => onOffsetReset(part.id)}>
                        reset to {formatOffset(suggested)}
                      </button>
                    </>
                  )}
                </div>

                {part.analysis.notes.map((note) => (
                  <div key={note} className="part__note">
                    {note}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function rolePillClass(role: FirmwarePart["analysis"]["role"]): string {
  switch (role) {
    case "app":
      return "pill--accent";
    case "bootloader":
      return "pill--info";
    case "partition-table":
      return "pill--info";
    case "merged":
      return "pill--success";
    case "unknown":
      return "pill--danger";
    default:
      return "pill--muted";
  }
}

function describeBasis(basis: FirmwarePart["analysis"]["basis"]): string {
  switch (basis) {
    case "content":
      return "its contents";
    case "filename":
      return "its filename";
    case "filename+content":
      return "name and contents";
    case "guess":
      return "guesswork — check this one";
  }
}
